import type { RefObject } from 'react';
import { useFrame } from '@react-three/fiber';
import { StudioEnvironment } from './StudioEnvironment';
import { WeatherEffects } from './WeatherEffects';
import { TireSlipEffects } from './TireSlipEffects';
import { useRuntimeWeatherRef } from './DynamicWeatherController';
import type { GraphicsQualityOption } from '../data/graphicsQualityOptions';
import type { TimeOfDayOption } from '../data/timeOfDayOptions';
import type { DrivingCourseOption } from '../data/courseOptions';
import type { VehicleState, VehicleSurfaceCondition } from '../model/vehicleDriving';
import type { CarBounds, StudioOption, WeatherOption } from '../types';

export interface DrivingSceneProps {
  studio: StudioOption;
  selectedWeather: WeatherOption;
  dynamicWeather: boolean;
  timeOfDay: TimeOfDayOption;
  isDriving: boolean;
  drivingCourse: DrivingCourseOption;
  drivingStateRef: RefObject<VehicleState>;
  graphicsQuality: GraphicsQualityOption;
  modelBounds: CarBounds;
  /**
   * 물리가 읽는 노면 조건. 같은 객체를 계속 갱신합니다.
   *
   * 새 객체를 넘기면 CarDriver가 매 전환 샘플마다 다시 렌더되므로, 식별자는 고정하고
   * 필드만 바꿉니다. 그래야 렌더와 물리가 같은 날씨 값을 같은 프레임에 씁니다.
   */
  surfaceRef: RefObject<VehicleSurfaceCondition>;
}

/**
 * 환경·강수·타이어 효과를 한 곳에서 구성합니다.
 *
 * Canvas **안**에 있어야 하는 이유: 동적 날씨 보간이 useFrame을 쓰고, 그 값을 페이지로
 * 끌어올리면 전환 중 옵션 패널까지 다시 렌더됩니다.
 */
export function DrivingScene({
  studio,
  selectedWeather,
  dynamicWeather,
  timeOfDay,
  isDriving,
  drivingCourse,
  drivingStateRef,
  graphicsQuality,
  modelBounds,
  surfaceRef,
}: DrivingSceneProps) {
  const weather = useRuntimeWeatherRef(selectedWeather, dynamicWeather);

  /*
   * 렌더 중이 아니라 프레임 루프에서 씁니다. 렌더 단계에서 ref를 바꾸면 React의 렌더
   * 순수성 가정이 깨지고(StrictMode 이중 렌더에서 값이 어긋납니다) 소비자도 어차피
   * 프레임에서 읽습니다. CarDriver는 priority −1이라 이 갱신을 같은 프레임에 봅니다.
   */
  useFrame(() => {
    const surface = surfaceRef.current;
    if (surface === null) {
      return;
    }
    surface.gripMultiplier = weather.gripMultiplier;
    surface.rollingResistanceMultiplier = weather.rollingResistanceMultiplier;
    surface.roadWetness = weather.roadWetness;
  }, -2);

  return (
    <>
      <StudioEnvironment
        studio={studio}
        weather={weather}
        timeOfDay={timeOfDay}
        flatGround={isDriving}
        drivingCourse={drivingCourse}
        anisotropy={graphicsQuality.anisotropy}
        environmentResolution={graphicsQuality.environmentResolution}
      />
      <WeatherEffects
        weather={weather}
        driving={isDriving}
        drivingStateRef={drivingStateRef}
        particleRatio={graphicsQuality.particleRatio}
      />
      {/* 슬립 흔적은 차량 그룹 밖 월드 공간에 남아야 차를 따라 움직이지 않습니다. */}
      {isDriving ? (
        <TireSlipEffects
          bounds={modelBounds}
          stateRef={drivingStateRef}
          weather={weather}
          particleRatio={graphicsQuality.particleRatio}
        />
      ) : null}
    </>
  );
}
